import { isWithinInterval } from "date-fns";
import Link from "next/link";
import { useTranslation } from "react-i18next";

import { useAuthenticatedUserClassrooms } from "@sol/classrooms";
import { interpolate, Route } from "@sol/routing";
import { getResourceIRI, IClassroomItem } from "@sol/sdk";
import { ArrowRight } from "@sol/uikit/core/icons/Arrows/ArrowRight";
import { Button } from "@sol/uikit/general/Button/Button";

import { ClassroomsList } from "@/entities/classroom";
import { Swiper } from "@/features/dashboards";

import { ClassroomSlide } from "./components/ClassroomSlide";
import { HomeCard, HomeCardDescription } from "./components/HomeCard";

export function TrainerHome() {
    const [t] = useTranslation();
    const { classrooms = [] } = useAuthenticatedUserClassrooms();

    return (
        <div className="grid grid-cols-1 gap-ant lg:grid-cols-2">
            <HomeCard className="col-span-1 row-span-1">
                <Swiper
                    items={classrooms.filter(c => {
                        const start = new Date(c.startDate);
                        const end = new Date(c.endDate);
                        return isWithinInterval(new Date(), { start: start, end: end }) || c.active;
                    })}
                    getItemKey={getResourceIRI}
                    renderItem={classroom => <ClassroomSlide classroom={classroom as IClassroomItem} />}
                />
            </HomeCard>
            <HomeCard
                title={t("component.home.trainer.trainer-workspace.title")}
                className="col-span-1 h-fit lg:row-span-2 lg:h-full"
            >
                <div className="flex min-h-0 w-full flex-col gap-6 lg:justify-between">
                    <HomeCardDescription label={t("component.home.trainer.trainer-workspace.label")} className="flex-1">
                        {t("component.home.trainer.trainer-workspace.description")}
                    </HomeCardDescription>

                    <div className="flex flex-col gap-3">
                        <HomeCard
                            className="rounded-ant border-ant-secondary bg-ant-fill-quaternary"
                            title={t("component.home.trainer.trainer-workspace.briefs.title")}
                            image={<img src="/static/home/media.svg" alt="" />}
                            action={
                                <Link href={interpolate(Route.TRAINER_WORKSPACE_BRIEFS, {})} passHref>
                                    <Button
                                        size="small"
                                        iconPosition="end"
                                        icon={<ArrowRight variant="antd" />}
                                        title={t("component.home.trainer.trainer-workspace.briefs.cta.title")}
                                        aria-label={t("component.home.trainer.trainer-workspace.briefs.cta.title")}
                                    >
                                        {t("component.home.trainer.trainer-workspace.briefs.cta.label")}
                                    </Button>
                                </Link>
                            }
                        >
                            <HomeCardDescription label={t("component.home.trainer.trainer-workspace.briefs.label")}>
                                {t("component.home.trainer.trainer-workspace.briefs.description")}
                            </HomeCardDescription>
                        </HomeCard>
                        <HomeCard
                            className="rounded-ant border-ant-secondary bg-ant-fill-quaternary"
                            title={t("component.home.trainer.trainer-workspace.professional-situations.title")}
                            image={<img src="/static/home/launch.svg" alt="" />}
                            action={
                                <Link href={interpolate(Route.TRAINER_WORKSPACE_PROFESSIONAL_SITUATION, {})} passHref>
                                    <Button
                                        size="small"
                                        iconPosition="end"
                                        icon={<ArrowRight variant="antd" />}
                                        title={t(
                                            "component.home.trainer.trainer-workspace.professional-situations.cta.title",
                                        )}
                                        aria-label={t(
                                            "component.home.trainer.trainer-workspace.professional-situations.cta.title",
                                        )}
                                    >
                                        {t(
                                            "component.home.trainer.trainer-workspace.professional-situations.cta.label",
                                        )}
                                    </Button>
                                </Link>
                            }
                        >
                            <HomeCardDescription
                                label={t("component.home.trainer.trainer-workspace.professional-situations.label")}
                            >
                                {t("component.home.trainer.trainer-workspace.professional-situations.description")}
                            </HomeCardDescription>
                        </HomeCard>
                        <HomeCard
                            className="rounded-ant border-ant-secondary bg-ant-fill-quaternary"
                            title={t("component.home.trainer.follow-ups.title")}
                            image={<img src="/static/home/growth.svg" alt="" />}
                            action={
                                <Link href={interpolate(Route.FOLLOW_UPS_LIST, {})} passHref>
                                    <Button
                                        size="small"
                                        iconPosition="end"
                                        icon={<ArrowRight variant="antd" />}
                                        title={t("component.home.trainer.follow-ups.cta.title")}
                                        aria-label={t("component.home.trainer.follow-ups.cta.title")}
                                    >
                                        {t("component.home.trainer.follow-ups.cta.label")}
                                    </Button>
                                </Link>
                            }
                        >
                            <HomeCardDescription label={t("component.home.trainer.follow-ups.label")}>
                                {t("component.home.trainer.follow-ups.description")}
                            </HomeCardDescription>
                        </HomeCard>
                    </div>
                </div>
            </HomeCard>
            <HomeCard title={t("component.home.manager.classrooms.title")} className="col-span-1">
                <div className="flex w-full flex-col">
                    <HomeCardDescription>{t("component.home.manager.classrooms.description")}</HomeCardDescription>
                    <ClassroomsList
                        className="max-h-[300px] w-full overflow-auto sm:max-h-[325px] lg:max-h-[370px]"
                        classrooms={classrooms.filter(c => {
                            const start = new Date(c.startDate);
                            const end = new Date(c.endDate);
                            return isWithinInterval(new Date(), { start: start, end: end }) || c.active;
                        })}
                    />
                </div>
            </HomeCard>
        </div>
    );
}
